import * as m from '@staiter/i18n';
import { createFileRoute } from '@tanstack/react-router';
import { Lock } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { z } from 'zod';
import { EmptyState } from '~/components/data';
import { Button } from '~/components/primitives';
import { TaskPeekPanel } from '~/components/tasks/peek/TaskPeekPanel';
import { ProjectLayoutRoot } from '~/components/tasks/views/ProjectLayoutRoot';
import { PROJECT_SETTINGS_SECTIONS } from '~/components/tasks/views/ProjectSettingsView';
import { useChatPanelStore } from '~/lib/chat/chatPanelStore';
import { useZenEscapeExit, useZenSync } from '~/lib/tasks/gantt/useZenMode';
import { useAssignableUsers, useProject, useProjectMembers } from '~/lib/tasks/queries/useTasks';
import { useTaskViewSwitcherStore } from '~/lib/tasks/stores/taskViewSwitcherStore';
import { useAuthStore } from '~/store/auth';
import { useUiStore } from '~/store/ui';

/**
 * Маршрут проекта task-module: List/Kanban/Gantt/Calendar/Settings
 * (FE-S12.2-22 + S17.1 F-A2 workspace surface + S17.3 F-C2 peek).
 * ?peek=<taskId> — side-peek любой задачи, URL-shareable.
 */

/**
 * FE-20 — параметры вида симметричны дашборду. Раньше в схеме был только `peek`, и
 * ссылка вида `/work-tasks/$id?group=priority&vt=board` теряла всё, кроме peek:
 * роутер молча выбрасывал незнакомые параметры, а поделиться видом было нельзя.
 * Значение проверяется реестром измерений (resolveGrouping) ДО применения.
 */
const SearchSchema = z.object({
  peek: z.string().uuid().optional().catch(undefined),
  vt: z.enum(['list', 'kanban', 'gantt', 'calendar', 'settings']).catch('list').optional(),
  group: z
    .enum([
      'state',
      'state_group',
      'due',
      'priority',
      'assignee',
      'author',
      'label',
      'hierarchy',
      // 'project' и 'entity' внутри проекта вырождены — реестр их скрывает, но в URL
      // они приходить могут (ссылка с дашборда). Принимаем и откатываем с объяснением.
      'project',
      'entity',
      'none',
    ])
    .optional()
    .catch(undefined),
  subgroup: z
    .enum(['state', 'assignee', 'priority', 'due', 'label', 'none'])
    .optional()
    .catch(undefined),
  show_empty: z.boolean().optional().catch(undefined),
  /**
   * D4.10 (Z-F6) — срез «задачи вне расчёта прогресса». Приходит по ссылке с
   * Hero-метрики карточки проекта: там названо число, здесь — сами записи.
   * Не правило `adhocFilter`: критерий задан сервером (знаменатель процента), и
   * пользователь его не собирает — он его открывает. Снимается чипом.
   */
  progress: z.literal('excluded').optional().catch(undefined),
  /**
   * CM-F3 (D4.11a) — якорь раздела настроек проекта. Пункты меню «Участники» /
   * «Видимость» / «Архивировать» живут в `vt=settings`, и без якоря меню
   * доводило только до страницы: нужный блок дальше искали глазами.
   */
  section: z.enum(PROJECT_SETTINGS_SECTIONS).optional().catch(undefined),
  /** Группа П (§6) — полноэкранный zen-режим канваса ганта (deep-link `?zen=1`). */
  zen: z
    .union([z.literal(1), z.literal(true)])
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute('/_auth/work-tasks/$projectId')({
  staticData: { minMainWidth: 792 },
  validateSearch: SearchSchema,
  component: WorkTasksProject,
});

function WorkTasksProject(): JSX.Element {
  const { projectId } = Route.useParams();
  const { peek, group, subgroup, show_empty, vt, zen, progress, section } = Route.useSearch();
  // F20 — id актора для карточки: без него корзина вложений и claim-логика
  // не знают, «я» это или нет (проп годами дефолтил в пустую строку).
  const currentUserId = useAuthStore((s) => s.user?.user_id ?? s.user?.id ?? '');
  const navigate = Route.useNavigate();
  // Группа П (§6) — zen из search-param: sync ui-флага + Esc-выход (проверка порталов).
  const zenActive = zen === 1 || zen === true;
  const toggleZen = useCallback(
    () => navigate({ search: (prev) => ({ ...prev, zen: prev.zen ? undefined : 1 }) }),
    [navigate],
  );
  const exitZen = useCallback(
    () => navigate({ search: (prev) => ({ ...prev, zen: undefined }) }),
    [navigate],
  );
  useZenSync(zenActive);
  useZenEscapeExit(zenActive, exitZen);
  const setStoredView = useTaskViewSwitcherStore((s) => s.setView);

  // FE-20 — вид из ссылки. Раньше `?vt=board` роутер просто выбрасывал (в схеме его не
  // было), и поделиться доской проекта было нельзя: получатель открывал свой последний
  // вид из стора.
  useEffect(() => {
    if (vt) setStoredView(projectId, vt);
  }, [vt, projectId, setStoredView]);
  const projectQuery = useProject(projectId);
  // BUG-3 — users-prop всех пикеров = пользователи компании (assignable), не
  // только участники проекта; участники добавляются для имён уже-назначенных.
  const membersQuery = useProjectMembers(projectId);
  const assignableQuery = useAssignableUsers(projectId);
  const pickerUsers = useMemo(() => {
    const byId = new Map<string, { id: string; name: string; email?: string }>();
    for (const u of assignableQuery.data ?? []) byId.set(u.id, u);
    for (const m of membersQuery.data ?? []) if (!byId.has(m.id)) byId.set(m.id, m);
    return [...byId.values()];
  }, [assignableQuery.data, membersQuery.data]);

  // F2-full+F3 (projects→task-module merge) — это full-bleed task-workspace проекта
  // (§3.4a). PM-карточка живёт на /projects/$id; её вкладка «Задачи» уводит сюда
  // (launcher). Дубль-навигации нет: задачи рендерятся только здесь, PM-данные — там.

  // S17.1 F-A4 — Designing for Intent: SideNav сворачивается при входе в workspace.
  // Уважаем ручной выбор: восстанавливаем только если свернули мы.
  const sidebarCollapsed = useUiStore((st) => st.sidebarCollapsed);
  const setSidebarCollapsed = useUiStore((st) => st.setSidebarCollapsed);
  const weCollapsedRef = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount/unmount only
  useEffect(() => {
    if (!sidebarCollapsed) {
      setSidebarCollapsed(true);
      weCollapsedRef.current = true;
    }
    return () => {
      if (weCollapsedRef.current) setSidebarCollapsed(false);
    };
  }, []);

  // project-visibility (F8) — закрытый/несуществующий проект (404/403) → EmptyState
  // «не найдено» вместо зависшего скелета. Текст одинаков для несуществующего и
  // приватного (инвариант 3: не раскрываем существование).
  const projectStatus = (projectQuery.error as { status?: number } | null)?.status;
  const projectNotFound = projectQuery.isError && (projectStatus === 404 || projectStatus === 403);

  // S17.3 F-C2 — peek через search-param: открытие из любого вида, Esc/закрыть — убрать
  const openPeek = (taskId: string) => navigate({ search: (prev) => ({ ...prev, peek: taskId }) });
  const chatFullscreen = useChatPanelStore((st) => st.fullscreen);
  const closePeek = () => navigate({ search: (prev) => ({ ...prev, peek: undefined }) });

  if (projectNotFound) {
    return (
      <EmptyState
        icon={<Lock size={32} />}
        title={m.project_workspace_not_found_title()}
        description={m.project_workspace_not_found_body()}
        action={
          <Button variant="secondary" onClick={() => navigate({ to: '/projects' })}>
            {m.project_workspace_back_to_list()}
          </Button>
        }
      />
    );
  }

  // FE-20 — вид живёт в URL (им можно поделиться) и переживает навигацию через
  // per-project стор. Санитайз делает реестр измерений внутри ProjectLayoutRoot:
  // невалидная для проекта ось откатывается с видимым уведомлением, а не молча.
  const setViewParam = (patch: {
    group?: string | undefined;
    subgroup?: string | undefined;
    show_empty?: boolean | undefined;
    vt?: string | undefined;
  }) =>
    navigate({
      search: (prev) => ({
        ...prev,
        ...('group' in patch ? { group: patch.group as never } : {}),
        ...('subgroup' in patch ? { subgroup: patch.subgroup as never } : {}),
        ...('show_empty' in patch ? { show_empty: patch.show_empty } : {}),
        ...('vt' in patch ? { vt: patch.vt as never } : {}),
      }),
      replace: true,
    });

  return (
    <ProjectLayoutRoot
      projectId={projectId}
      title={projectQuery.data?.name}
      users={pickerUsers}
      onOpenTask={openPeek}
      {...(group ? { group } : {})}
      {...(subgroup ? { subgroup } : {})}
      {...(show_empty !== undefined ? { showEmpty: show_empty } : {})}
      onViewChange={setViewParam}
      {...(progress ? { progressScope: progress } : {})}
      {...(section ? { settingsSection: section } : {})}
      onProgressScopeClear={() =>
        navigate({ search: (prev) => ({ ...prev, progress: undefined }), replace: true })
      }
      zen={zenActive}
      onToggleZen={toggleZen}
      aside={
        // chat-panel-geometry F-16 [Г5] — под развёрнутым чатом быстрый просмотр
        // не монтируется: панель занимает экран целиком.
        peek && !chatFullscreen ? (
          <TaskPeekPanel
            taskId={peek}
            users={pickerUsers}
            currentUserId={currentUserId}
            onClose={closePeek}
          />
        ) : undefined
      }
    />
  );
}
